<div class="flex justify-center bg-white">
  <div class="w-full max-w-[1600px] px-6 py-16">

    <!-- Header -->
    <div class="mb-16">
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        
        <!-- Left: Title Section -->
        <div class="max-w-3xl">
          <span class="inline-block mb-3 px-4 py-1 text-sm font-semibold rounded-full 
                       bg-blue-100 text-blue-700">
            Curated • Handpicked
          </span>

          <h2 class="text-5xl md:text-6xl font-black tracking-tight text-gray-900">
            Premium Stories
          </h2>

          <div class="mt-4 h-1.5 w-40 rounded-full 
                      bg-gradient-to-r from-blue-600 via-yellow-400 to-yellow-500">
          </div>

          <p class="mt-6 text-lg text-gray-600">
            Deep insights, powerful narratives, and stories that truly matter.
          </p>
        </div>

        <!-- Right: Search & See All -->
        <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 lg:min-w-[400px]">
          
          <!-- Search Input -->
          <div class="relative flex-1">
            <mat-icon class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              search
            </mat-icon>
            <input
              type="text"
              [(ngModel)]="searchQuery"
              (ngModelChange)="onSearchChange()"
              placeholder="Search stories..."
              class="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 
                     focus:border-blue-500 focus:outline-none focus:ring-2 
                     focus:ring-blue-100 transition-all duration-300
                     text-gray-900 placeholder-gray-400"
            />
            
            @if (searchQuery) {
              <button
                (click)="clearSearch()"
                class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 
                       hover:text-gray-600 transition-colors">
                <mat-icon>close</mat-icon>
              </button>
            }
          </div>

          <!-- See All Button -->
          <button
            (click)="seeAllStories()"
            class="flex items-center justify-center gap-2 px-6 py-3 
                   bg-blue-600 hover:bg-blue-700 text-white font-semibold 
                   rounded-xl shadow-lg hover:shadow-xl transition-all duration-300
                   whitespace-nowrap">
            <mat-icon>grid_view</mat-icon>
            See All
          </button>
        </div>

      </div>
    </div>

    <!-- Search Results Info -->
    @if (searchQuery && !loading) {
      <div class="mb-6 flex items-center gap-3 text-gray-600">
        <mat-icon class="text-blue-600">info</mat-icon>
        <span>
          Found <strong class="text-gray-900">{{ filteredStories.length }}</strong> 
          {{ filteredStories.length === 1 ? 'story' : 'stories' }} 
          matching "<strong class="text-gray-900">{{ searchQuery }}</strong>"
        </span>
        <button
          (click)="clearSearch()"
          class="ml-auto text-blue-600 hover:text-blue-700 font-semibold 
                 transition-colors">
          Clear search
        </button>
      </div>
    }

    <!-- Loading Skeleton -->
    @if (loading) {
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        @for (i of [1,2,3]; track i) {
          <div class="animate-pulse rounded-2xl bg-white shadow-lg p-6">
            <div class="h-72 bg-gray-200 rounded-lg mb-6"></div>
            <div class="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        }
      </div>
    }

    <!-- Empty State -->
    @else if (filteredStories.length === 0) {
      <div class="py-32 text-center">
        <mat-icon class="text-gray-300 text-[96px] mb-6">
          {{ searchQuery ? 'search_off' : 'auto_stories' }}
        </mat-icon>
        <h3 class="text-3xl font-bold text-gray-800">
          {{ searchQuery ? 'No stories found' : 'No stories yet' }}
        </h3>
        <p class="mt-3 text-gray-500">
          {{ searchQuery 
            ? 'Try adjusting your search terms or browse all stories' 
            : 'New premium stories are coming soon.' 
          }}
        </p>
        @if (searchQuery) {
          <button
            (click)="clearSearch()"
            class="mt-6 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white 
                   font-semibold rounded-xl transition-all duration-300">
            Clear Search
          </button>
        }
      </div>
    }

    <!-- Carousel -->
    @else {
      <div class="relative group">

        @if (showNavigationArrows) {
          <!-- Previous Arrow -->
          <button 
            (click)="previousSlide()"
            [disabled]="!canNavigatePrev"
            [class.opacity-50]="!canNavigatePrev"
            [class.cursor-not-allowed]="!canNavigatePrev"
            class="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white 
                   hover:bg-gray-100 text-gray-800 p-4 rounded-full shadow-lg 
                   transition-all duration-300 disabled:hover:bg-white group">
            <mat-icon class="text-3xl group-hover:scale-110 transition-transform">
              chevron_left
            </mat-icon>
          </button>

          <!-- Next Arrow -->
          <button 
            (click)="nextSlide()"
            [disabled]="!canNavigateNext"
            [class.opacity-50]="!canNavigateNext"
            [class.cursor-not-allowed]="!canNavigateNext"
            class="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white 
                   hover:bg-gray-100 text-gray-800 p-4 rounded-full shadow-lg 
                   transition-all duration-300 disabled:hover:bg-white group">
            <mat-icon class="text-3xl group-hover:scale-110 transition-transform">
              chevron_right
            </mat-icon>
          </button>
        }

        <!-- Track -->
        <div class="overflow-hidden px-14">
          <div
            class="flex transition-transform duration-700 ease-[cubic-bezier(.22,.61,.36,1)]"
            [style.transform]="'translateX(-' + (currentIndex * slideWidth) + '%)'">

            @for (story of filteredStories; track story.id) {
              <div class="flex-shrink-0 px-4" [style.width]="slideWidth + '%'">
                <article
                  (click)="openStory(story)"
                  class="group bg-gray-100 rounded-xl 
                         hover:shadow-xl transition-all duration-500 cursor-pointer">

                  <!-- Image -->
                  <div class="relative h-80 overflow-hidden">
                    <img
                      [src]="'https://epaper.all.tz/backend/uploads/media/' + story.thumbnail"
                      (error)="onImageError($event)"
                      class="h-full w-full object-cover transition-transform duration-700 
                             group-hover:scale-110"
                    />

                    <!-- Overlay -->
                    <div class="absolute inset-0 bg-gradient-to-t 
                                from-black/70 via-black/20 to-transparent">
                    </div>
                  </div>

                  <!-- Content -->
                  <div class="p-6 space-y-4">
                    <h3 class="text-xl font-bold text-gray-900 leading-snug 
                               line-clamp-2 min-h-[1.5rem]">
                      {{ story.title }}
                    </h3>

                    <p class="text-gray-600 line-clamp-2 min-h-[3rem]">
                      {{ story.subtitle }}
                    </p>

                    <!-- Meta -->
                    <div class="flex items-center justify-between text-sm text-gray-500">
                      <span>By {{ story.author }}</span>
                      <span>{{ story.readTime }} min read</span>
                    </div>

                    <!-- CTA -->
                    <button
                      class="mt-4 w-full flex items-center justify-center gap-2
                             bg-transparent hover:bg-blue-700 text-blue-600 
                             hover:text-white py-3 rounded-xl font-semibold
                             transition-all duration-300 border-2 border-blue-600">
                      <mat-icon>menu_book</mat-icon>
                      Read Story
                    </button>
                  </div>
                </article>
              </div>
            }
          </div>
        </div>

        <!-- Dots -->
        <div class="mt-10 flex justify-center gap-3">
          @for (item of filteredStories; track item.id; let i = $index) {
            @if (i % visibleCount === 0) {
              <button
                (click)="currentIndex = i"
                class="h-3 rounded-full transition-all"
                [class.w-8]="currentIndex === i"
                [class.bg-blue-600]="currentIndex === i"
                [class.w-3]="currentIndex !== i"
                [class.bg-gray-300]="currentIndex !== i">
              </button>
            }
          }
        </div>
      </div>
    }
  </div>
</div>